import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { MultiSelect } from "primereact/multiselect";
import { convertIsoStringToDate, isRtl, getFieldName } from "src/utils/helpers";
import { Calendar } from "primereact/calendar";
import { getEngineerTechnician } from "src/redux/slices/assignBuildingSlice";
import moment from "moment";
import { toast } from "react-toastify";
import { Dropdown } from "primereact/dropdown";
import {
  getATECasesStatistics,
  getBuildingsStatistics,
  getContractInformationStatistics,
  getDashboardCards,
  getMaintenanceAppointmentsStatistics,
  getBMCRatingStatistics,
  setFilterData,
  resetFilterData,
} from "src/redux/slices/dashboardSlice";
import FilterActionButtons from "src/components/FilterActionButtons/FilterActionButtons";
import ToggleFilter from "src/components/ToggleFilter/ToggleFilter";
import useResize from "src/hooks/useResize";
import { onHideCalendar, onShowCalendar } from "src/utils/common";
import {
  getServiceTerritories,
  setServiceTerritories,
} from "src/redux/slices/lookupSlice";

const CDDashboardFilter = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common", "dashboard"]);

  const creationDateFromRef = useRef<Calendar | null>(null);
  const creationDateToRef = useRef<Calendar | null>(null);

  const { isMobile } = useResize();
  const [isFilterShown, setIsFilterShown] = useState(!isMobile);

  const bmcList = useSelector((state: any) => state.civilDefense.bmcList);
  const serviceTerritories = useSelector(
    (state: any) => state.lookup.serviceTerritories
  );
  const citiesOptions = useSelector((state: any) => state.lookup.citiesOptions);

  const language = useSelector((state: any) => state.lookup.language);
  const { startDate, endDate, territoryIds, companyIds, city } = useSelector(
    (state: any) => state.dashboard.filterData
  );
  const handleFilter = () => {
    dispatch(getATECasesStatistics() as any).then(() => {});
    dispatch(getDashboardCards() as any).then(() => {});
    dispatch(getBuildingsStatistics() as any).then(() => {});
    dispatch(getContractInformationStatistics() as any).then(() => {});
    dispatch(getBMCRatingStatistics() as any).then(() => {});
    dispatch(getMaintenanceAppointmentsStatistics() as any).then(() => {});
  };

  const handleFormFieldChange = (e: any) => {
    let name, value;
    if (e.target) {
      name = e.target.name;
      value = e.target.value;
    } else {
      name = e.name;
      value = e.value;
    }
    dispatch(setFilterData({ field: name, value }));
    if (name === "startDate" && endDate) {
      const startDateValue = moment(value);
      const endDateValue = moment(endDate);
      if (startDateValue.isAfter(endDateValue)) {
        toast.error(t("startEndDateValidation"));
        dispatch(setFilterData({ field: "endDate", value }));
      }
    }
  };

  const handleCityChange = (e: any) => {
    handleFormFieldChange(e);
    dispatch(setServiceTerritories([]));
    dispatch(setFilterData({ field: "territoryIds", value: null }));
    dispatch(getServiceTerritories(false, e.value) as any);
  };

  const handleClearFilterForm = (): void => {
    dispatch(resetFilterData());
    dispatch(getEngineerTechnician() as any);
    handleFilter();
  };
  const toggleFilter = () => {
    setIsFilterShown(!isFilterShown);
  };
  return (
    <>
      <ToggleFilter onClick={toggleFilter} />
      <div
        className={`pt-3 maintenance-requests-filter ${
          !isMobile || isFilterShown ? "d-flex" : "d-none"
        }`}
      >
        <p className="d-flex mt-1 me-2 ms-2 mb-2 align-items-start fit-content filter-by-label">
          {t("filterBy")}
        </p>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="filter-row mb-0">
            {/* <Tooltip target=".status-dropdown" /> */}
            <MultiSelect
              filter
              resetFilterOnHide
              id="companyIds"
              name="companyIds"
              value={companyIds}
              className="status-dropdown"
              optionValue={"id"}
              optionLabel={"name"}
              onChange={handleFormFieldChange}
              options={bmcList}
              placeholder={t("company")}
              panelClassName="status-dropdown-panel"
            />
            <Dropdown
              id="city"
              value={city}
              name="city"
              options={citiesOptions}
              optionLabel={getFieldName(language)}
              optionValue="cityId"
              onChange={handleCityChange}
              required
              placeholder={t("selectCity")}
            />
            <MultiSelect
              filter
              resetFilterOnHide
              emptyMessage={t("noResultsFound")}
              emptyFilterMessage={t("noResultsFound")}
              options={city ? serviceTerritories : []}
              placeholder={t("territory") + " - " + t("selectCityFirstShort")}
              className="status-dropdown"
              optionValue={"territoryId"}
              optionLabel={getFieldName(language)}
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={territoryIds}
              name="territoryIds"
            />
            <Calendar
              value={startDate || moment().subtract(7, "days").toDate()}
              onChange={handleFormFieldChange}
              readOnlyInput
              placeholder={t("creationDateFrom")}
              name="startDate"
              dateFormat="dd/mm/yy"
              showIcon
              ref={creationDateFromRef}
              onHide={() => onHideCalendar(creationDateFromRef)}
              onShow={() => onShowCalendar(creationDateFromRef)}
              key={`startDate-${startDate?.toString()}`}
            />

            <Calendar
              value={endDate || new Date()}
              onChange={handleFormFieldChange}
              readOnlyInput
              placeholder={t("creationDateTo")}
              name="endDate"
              dateFormat="dd/mm/yy"
              minDate={
                !startDate
                  ? new Date(-8640000000000000)
                  : convertIsoStringToDate(startDate)
              }
              ref={creationDateToRef}
              onHide={() => onHideCalendar(creationDateToRef)}
              onShow={() => onShowCalendar(creationDateToRef)}
              key={`endDate-${endDate?.toString()}`}
            />
            <FilterActionButtons
              handleFilter={handleFilter}
              handleClear={handleClearFilterForm}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default CDDashboardFilter;
